import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'antd';
import { Button, ButtonType } from 'syngenta-digital-cropwise-react-ui-kit';
import SortIcon from 'assets/sort.svg?react';
import {
  SORT_BY_VALUES,
  SORT_ORDER_VALUES,
  SortConfig
} from 'screens/Applications&WorkOrders/Components/types';
import { useOperationSortContext } from 'screens/Applications&WorkOrders/Context/SortContext';

const BUTTON_SIZE = 'middle' as const;

const DropdownMenuItem = ({
  label,
  sorts: optionSorts
}: {
  label: string;
  sorts: SortConfig[];
}) => {
  const { sorts, setSort } = useOperationSortContext();
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Check if this option is selected (compare the entire sort array)
  const isSelected = JSON.stringify(sorts) === JSON.stringify(optionSorts);

  const handleClick = () => {
    setSort(optionSorts);
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    buttonRef.current?.focus();
    e.currentTarget.style.backgroundColor = isSelected ? '#C6E6FF' : '#F3F4F6';
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.backgroundColor = '';
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
      return;
    }

    // Handle arrow key navigation
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();

      // Find the dropdown overlay
      const dropdownOverlay = document.querySelector(
        '.header-action-buttons-dropdown'
      );
      if (!dropdownOverlay) {
        return;
      }

      // Get all buttons in the dropdown
      const buttons = Array.from<HTMLButtonElement>(
        dropdownOverlay.querySelectorAll('button[type="button"]')
      );

      if (buttons.length === 0) {
        return;
      }

      // Find current button index
      const currentIndex = buttons.indexOf(e.currentTarget);

      if (e.key === 'ArrowDown') {
        const nextIndex =
          currentIndex < buttons.length - 1 ? currentIndex + 1 : 0;
        buttons[nextIndex]?.focus();
      } else if (e.key === 'ArrowUp') {
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : buttons.length - 1;
        buttons[prevIndex]?.focus();
      }
    }
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      className={`w-full px-3 py-1 text-sm text-left focus:outline-none ${
        isSelected ? 'bg-info-light-blue font-semibold' : ''
      }`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
    >
      {label}
    </button>
  );
};

const SORT_OPTIONS: Array<{
  key: string;
  label: string;
  sorts: SortConfig[];
}> = [
  {
    key: 'sort-asc-start-date',
    label: 'Start Date (New→Old)',
    sorts: [
      { sortBy: SORT_BY_VALUES.START_DATE, order: SORT_ORDER_VALUES.DESC },
      { sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.ASC }
    ]
  },
  {
    key: 'sort-desc-start-date',
    label: 'Start Date (Old→New)',
    sorts: [
      { sortBy: SORT_BY_VALUES.START_DATE, order: SORT_ORDER_VALUES.ASC },
      { sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.ASC }
    ]
  },
  {
    key: 'sort-asc-record-name',
    label: 'Record Name (A→Z)',
    sorts: [
      { sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.ASC },
      { sortBy: SORT_BY_VALUES.START_DATE, order: SORT_ORDER_VALUES.DESC }
    ]
  },
  {
    key: 'sort-desc-record-name',
    label: 'Record Name (Z→A)',
    sorts: [
      { sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.DESC },
      { sortBy: SORT_BY_VALUES.START_DATE, order: SORT_ORDER_VALUES.DESC }
    ]
  },
  {
    key: 'sort-asc-creation-date',
    label: 'Creation Date (New→Old)',
    sorts: [
      { sortBy: SORT_BY_VALUES.CREATION_DATE, order: SORT_ORDER_VALUES.DESC },
      { sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.ASC }
    ]
  },
  {
    key: 'sort-desc-creation-date',
    label: 'Creation Date (Old→New)',
    sorts: [
      { sortBy: SORT_BY_VALUES.CREATION_DATE, order: SORT_ORDER_VALUES.ASC },
      { sortBy: SORT_BY_VALUES.NAME, order: SORT_ORDER_VALUES.ASC }
    ]
  }
];

const SortDropdownMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { sorts } = useOperationSortContext();

  const selectedOption = SORT_OPTIONS.find(
    option => JSON.stringify(option.sorts) === JSON.stringify(sorts)
  );
  const selectedLabel = selectedOption
    ? selectedOption.label.split(' (')[0]
    : 'Creation Date';

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    // Small delay to ensure the dropdown is fully rendered in the DOM
    const timer = setTimeout(() => {
      // Find the dropdown overlay and get the first button inside it
      const dropdownOverlay = document.querySelector(
        '.header-action-buttons-dropdown'
      );
      if (dropdownOverlay) {
        const firstButton = dropdownOverlay.querySelector(
          'button[type="button"]'
        ) as HTMLButtonElement;
        if (firstButton) {
          firstButton.focus();
        }
      }
    }, 0);

    return () => {
      clearTimeout(timer);
    };
  }, [isOpen]);

  return (
    <Dropdown
      menu={{
        items: SORT_OPTIONS.map(option => ({
          key: option.key,
          label: <DropdownMenuItem label={option.label} sorts={option.sorts} />
        }))
      }}
      trigger={['click']}
      placement="bottomRight"
      overlayClassName="header-action-buttons-dropdown"
      onOpenChange={setIsOpen}
      open={isOpen}
    >
      <Button
        type={ButtonType.outline}
        size={BUTTON_SIZE}
        className={`flex items-center ${isOpen ? 'bg-neutral-20' : ''}`}
        style={{ width: '166px', padding: '0 10px', gap: '4px' }}
      >
        <SortIcon style={{ width: '18px', height: '18px', flexShrink: 0 }} />
        <span
          style={{
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            letterSpacing: '-0.105px'
          }}
        >
          <span>Sort:</span>
          <span style={{ marginLeft: '3px' }}>{selectedLabel}</span>
        </span>
      </Button>
    </Dropdown>
  );
};

export default SortDropdownMenu;
